import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root'
})
export class UserJourneyService {
  /**
* baseApiUrl property
* @type {string}
*/
  private baseApiUrl: string = environment.apiUrl + 'manager/journey';
  /**
* A BehaviorSubject that holds the steps in the user journey process.
*/
  private _steps$ = new BehaviorSubject<UserStep[]>([]);
  /**
 * inject service
 * @param http HttpClient
 */
  private http = inject(HttpClient);

  /**
   * ReplaySubject to signal when a tour should start
   * Emits the actionKey of the tour to start
   * ReplaySubject(1) ensures late subscribers receive the last emitted value
   */
  private _startTour$ = new ReplaySubject<string>(1);


  /**
   * Returns an observable of the steps.
   */
  subJourneySteps(): Observable<any> {
    return this._steps$;
  }

  /**
 * sets the steps.
 * @param steps The steps to set.
 */
  setJourneySteps(steps: UserStep[]): void {
    this._steps$.next(steps);
  }

  /**
   * Returns an observable that emits when a tour should start
   */
  onStartTour(): Observable<string> {
    return this._startTour$.asObservable();
  }

  /**
   * Signals that a tour should start for the given actionKey
   * @param actionKey The action key of the tour to start
   */
  requestTourStart(actionKey: string): void {
    this._startTour$.next(actionKey);
  }

  /**
 * call the server to get the steps in the userJourney
 * @returns {Promise<any>} 
 */
  getUserJourney(): Promise<any> {
    return firstValueFrom(this.http.get<UserJourneyrponseApiData>(this.baseApiUrl));
  }

  /**
 * Updates the user's profile photo.
 * @param data The object containing action key to update and set it as completed.
 * @returns {Promise<any>}
 */
  updateUserJourney(data): Promise<any> {
    return firstValueFrom(this.http.put(this.baseApiUrl, data));
  }

  /**
 * Marks a specific step or sub-step as completed.
 *
 * @param stepName - The action key of the step to complete.
 * @param subStepName - (Optional) The action key of the sub-step to complete.
 * @param data - (Optional).
 */
  completeStep(stepName: string, subStepName?: string, data?: string) {
    const updatedSteps = this._steps$.value.map(step => {
      if (step.actionKey === stepName) {
        if (subStepName && step.subSteps) {
          step.subSteps = step.subSteps.map(subStep =>
            subStep.actionKey === subStepName ? { ...subStep, isCompleted: true, data } : subStep
          );
          const allCompleted = step.subSteps.every(sub => sub.isCompleted);
          return { ...step, isCompleted: allCompleted };
        }
        return { ...step, isCompleted: true };
      }
      return step;
    });

    this._steps$.next(updatedSteps);
  }

  /**
 * Finds the next incomplete step or sub-step from a list of user steps.
 *
 * @param steps - An array of user steps to search through.
 * @returns The next incomplete UserStep or sub-step, or null if all are completed.
 */
  findNextIncompleteStep(steps: UserStep[]): UserStep | null {
    for (const step of steps) {
      if (!step.isCompleted) return step;
      if (step.subSteps) {
        const nextSubStep = step.subSteps.find(sub => !sub.isCompleted);
        if (nextSubStep) return nextSubStep;
      }
    }
    return null;
  }

  /**
   * Checks if a specific onboarding step or sub-step is completed.
   *
   * @param actionKey - The key identifying the onboarding step ('watch_video' or 'add_user').
   * @returns `true` if the step or sub-step is completed, otherwise `false`.
   */
  isOnboardingStepsCompleted(actionKey: string): boolean {
    if (actionKey === 'watch_video') {
      return this._steps$.getValue()[0]?.isCompleted
    }
    if (this._steps$.getValue()[1].subSteps[0]) {
      if (actionKey === 'add_user') {
        return this._steps$.getValue()[1]?.subSteps[0].isCompleted
      }
    }
  }

  /**
 * Get the action key of the next incomplete sub-step in the second onboarding step.
 *
 * @returns The `actionKey` of the next incomplete sub-step, or `null` if all sub-steps are completed or not available.
 */
  getNextStep(): string | null {
    const steps = this._steps$.getValue();
    if (steps[1]) {
      const nextSubStep = steps[1].subSteps.find(sub => !sub.isCompleted);
      if (nextSubStep) {
        return nextSubStep.actionKey;
      }
      return null
    }
  }

  /**
 * Checks whether the first sub-step of the second onboarding step is completed.
 *
 * @returns `true` if the first sub-step exists and is marked as completed; otherwise, `false`.
 */
  isFirstStepCompleted(): boolean {
    if (this._steps$.getValue()[1].subSteps[0]) {
      return this._steps$.getValue()[1].subSteps[0].isCompleted
    }
  }

  /**
 * Checks whether a specific sub-step in the second onboarding step is completed.
 *
 * @param actionKey 
 * @returns `true` if the sub-step is completed or not found; `false` if it exists and is not completed.
 */
  stepIsCompleted(actionKey: string) {
    const step = this._steps$.getValue()[1]?.subSteps.find(sub => sub.actionKey === actionKey);
    if (step)
      return step.isCompleted;
    return true;
  }

  /**
 * Get the `data` for its  specific sub-step by its action key.
 *
 * @param actionKey - The unique identifier of the sub-step.
 * @returns The `data` string from the matched sub-step, or `null` if not found.
 */
  getDataStep(actionKey: string): string {
    for (const step of this._steps$.value) {
      if (step.subSteps.length > 0) {
        const stepToreturn = step.subSteps.find(step => {
          return step.actionKey === actionKey
        })
        if (stepToreturn) {
          return stepToreturn.data
        }
      }
    }
    return null;
  }

}

export interface UserStep {
  id?: number;
  actionKey: string;
  isCompleted: boolean;
  subSteps?: UserStep[];
  data?: string;
}

interface UserJourneyrponseApiData {
  message: string;
  success: number;
  data: UserStep[]
}



